import {
  AddEventListernerResult,
  Colors,
  useEventListenersRef,
  useMediaStreamPlaysSound,
  useRefAutoUpdate,
  Video,
  VideoEventListenerMap,
  WithNativeHTMLProps,
} from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ReactComponent as IconNoAudio } from "../../assets/icons/blackCutMicrophone.svg";
import { ReactComponent as IconNoVideo } from "../../assets/icons/no-video.svg";

import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { PlayerContainerRenderer } from "./interfaces";
import { MeetingPlayerDataOverlay } from "./MeetingPlayerDataOverlay";
import MeetingPlayerSoundEffectOverlay from "./MeetingPlayerSoundEffectOverlay";
import styles from "./MeetingSessionMediaStreamPlayer.module.css";

export interface MeetingSessionMediaStreamPlayerEventListenerMap {
  playingSound: (playing: boolean) => void;
}

export interface MeetingSessionMediaStreamPlayerProps extends WithNativeHTMLProps<HTMLDivElement> {
  meeting: Meeting;
  sessions: MeetingSession[];
  sessionId: string;
  mediaSessionId?: string;
  mediaStream?: MediaStream;
  videoIsDisabled?: boolean;
  muted?: boolean;
  hideSoundEffect?: boolean;
  forceUseSession?: MeetingSession;
  callbacks?: Partial<MeetingSessionMediaStreamPlayerEventListenerMap>;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  videoStyle?: React.CSSProperties;
  renderCustomContainer?: PlayerContainerRenderer;
}

export interface MeetingSessionMediaStreamPlayerRef {
  isPlayingSound: () => boolean;
  addEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
    key: K,
    callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
  ) => AddEventListernerResult<MeetingSessionMediaStreamPlayerEventListenerMap, K>;
  removeEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
    key: K,
    callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
  ) => void;
}

const MemoVideo = React.memo(Video);

const MeetingSessionMediaStreamPlayer = React.forwardRef(
  (
    {
      meeting,
      sessions,
      sessionId,
      mediaSessionId,
      mediaStream,
      videoIsDisabled,
      muted,
      hideSoundEffect,
      forceUseSession,
      callbacks,
      children,
      className,
      style,
      videoStyle,
      renderCustomContainer,
      nativeProps,
    }: MeetingSessionMediaStreamPlayerProps,
    forwardRef: ForwardedRef<MeetingSessionMediaStreamPlayerRef>
  ) => {
    const eventListenersRef = useEventListenersRef<keyof MeetingSessionMediaStreamPlayerEventListenerMap, MeetingSessionMediaStreamPlayerEventListenerMap>();
    const callbacksRef = useRefAutoUpdate(callbacks);
    const session = useMemo(() => forceUseSession ?? sessions?.find((s) => s.id === sessionId), [sessions, sessionId, forceUseSession]);
    const mediaSession = useMemo(() => session?.medias?.find((m) => m.id === mediaSessionId), [session, mediaSessionId]);
    const isPlayingSound = useMediaStreamPlaysSound(
      getAudioContextForVolumeAnalyzer(),
      mediaStream,
      {
        samplingInterval: 300, // (ms) 80 -> 300
        analyzer: {
          samplingInterval: 300, // (ms) 80 -> 300
          fftSize: 256, // (samples) 1024 -> 256
          freqRange: "voice",
          // cloneMedia: false, // remote streams don't need cloning for analysis
        },
      },
    );
    const [videoIsReady, setVideoIsReady] = useState<boolean>(true);
    const videoCallbacks = useMemo<Partial<VideoEventListenerMap>>(
      () => ({
        ready: () => {
          setVideoIsReady(true);
        },
      }),
      [setVideoIsReady]
    );

    const videoStyles = useMemo<React.CSSProperties>(() => ({ 
      objectFit: "contain",
      ...(videoStyle ?? {}),
    }), [videoStyle]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------   EVENTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const triggerEvent = <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
      key: K,
      ...args: Parameters<MeetingSessionMediaStreamPlayerEventListenerMap[K]>
    ) => {
      (callbacksRef.current?.[key] as any)?.call(null, ...args);
      eventListenersRef.current?.triggerEvent(key, ...args);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useImperativeHandle(
      forwardRef,
      () => ({
        isPlayingSound: () => isPlayingSound,
        addEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
          key: K,
          callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
        ) => eventListenersRef.current?.addEventListener(key, callback),
        removeEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
          key: K,
          callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
        ) => eventListenersRef.current?.removeEventListener(key, callback),
      }),
      [isPlayingSound]
    );

    useEffect(() => {
      if (!mediaStream) setVideoIsReady(false);
    }, [mediaStream]);

    useEffect(() => {
      triggerEvent("playingSound", isPlayingSound);
    }, [isPlayingSound]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const renderContent = () => {
      const classes = ["meetingsessionmediaplayer-container", styles.container];
      if (className) classes.push(className);
      //if (!hideSoundEffect && isPlayingSound) classes.push(styles.volumeEffect);
      //classes.push(styles.volumeEffect);
      return (
        <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style}>
          <MemoVideo
            poster={mediaSession?.mediaThumbnail}
            autoPlay
            controls={false}
            className={styles.video}
            src={mediaStream}
            muted={muted}
            callbacks={videoCallbacks}
            style={videoStyles}
            playsInline
          />
          <MeetingPlayerDataOverlay
            meeting={meeting}
            session={session}
            mediaSession={mediaSession}
            mediaStream={mediaStream}
            videoIsDisabled={videoIsDisabled}
            videoIsReady={videoIsReady}
          />
          {children}
          <MeetingPlayerSoundEffectOverlay enabled={!hideSoundEffect && isPlayingSound} />
        </div>
      );
    };

    if (renderCustomContainer) {
      return <>{renderCustomContainer(renderContent(), { isPlayingSound })}</>;
    }

    return renderContent();
  }
);

export default MeetingSessionMediaStreamPlayer;
